import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Bookmark, Flag, Heart, MessageCircle, Repeat2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSavedPosts } from "@/scripts/SavedPostsContext";
import { useSocialSavedPosts } from "@/scripts/SocialSavedPostsContext";
import { useAuth } from "@/scripts/AuthContext";
import SharePostToDmModal from "./SharePostToDmModal";
import SharePostToSocialDmModal from "./SharePostToSocialDmModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SocialQuoteRepostDialog from "./SocialQuoteRepostDialog";
import SocialReportDialog from "./SocialReportDialog";
import { toastError, toastSuccess } from "@/lib/toast";
import { useSocialWriteApis } from "@/lib/socialWriteApis";

const PostInteractions = ({
  postId,
  likeCount = 0,
  commentCount = 0,
  repostCount = 0,
  onLike,
  onComment,
  accessToken,
  variant = "inline",
  className,
  isLiked = false,
  viewerHasReposted = false,
  post = null,
  orgId = null,
  onSavedChange = null,
  /** Enable social bare/quote repost controls */
  socialRepost = false,
  onRepostChange = null,
  onQuoteCreated = null,
}) => {
  const { user } = useAuth();
  const writeApis = useSocialWriteApis();
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [repostBusy, setRepostBusy] = useState(false);
  const [localReposted, setLocalReposted] = useState(viewerHasReposted);
  const [localRepostCount, setLocalRepostCount] = useState(repostCount);
  const { ensureStatus, isSaved, toggleSaved } = useSavedPosts();
  const {
    ensureStatus: ensureSocialStatus,
    isSaved: isSocialSaved,
    toggleSaved: toggleSocialSaved,
  } = useSocialSavedPosts();

  useEffect(() => {
    setLocalReposted(viewerHasReposted);
  }, [viewerHasReposted]);

  useEffect(() => {
    setLocalRepostCount(repostCount);
  }, [repostCount]);

  const resolvedOrgId = useMemo(() => {
    if (orgId) return String(orgId);
    const p = post;
    if (!p) return null;
    return (
      p.orgId ||
      p.org_id ||
      p.org?.org_id ||
      p.org?.orgId ||
      p.team?.org_id ||
      p.team?.orgId ||
      null
    );
  }, [orgId, post]);

  const resolvedPostId = useMemo(() => {
    if (postId != null) return String(postId);
    if (!post) return null;
    const id = post.post_id ?? post.postId ?? post.social_post_id ?? post.id;
    return id != null ? String(id) : null;
  }, [postId, post]);

  const rootPostForRepost = useMemo(() => {
    if (!post) return null;
    // Amplify the card being acted on (quote wrappers keep their own id).
    return post;
  }, [post]);

  const rootRepostId =
    rootPostForRepost?.social_post_id ??
    rootPostForRepost?.post_id ??
    resolvedPostId;

  const authorUserId =
    post?.user_id ?? post?.userId ?? post?.user?.user_id ?? null;
  const authorOrgId = post?.author_org_id ?? post?.authorOrgId ?? null;
  const isOwnPost = writeApis.isActAsOrg
    ? !!(
        writeApis.actAsOrgId &&
        authorOrgId &&
        String(authorOrgId) === String(writeApis.actAsOrgId)
      )
    : !!(
        user?.user_id &&
        authorUserId &&
        String(user.user_id) === String(authorUserId)
      );

  const isSocialPost = useMemo(() => {
    if (socialRepost) return true;
    const p = post;
    if (!p) return false;
    if (p.social_post_id != null || p.socialPostId != null) return true;
    if (resolvedPostId && !resolvedOrgId) return true;
    return false;
  }, [socialRepost, post, resolvedPostId, resolvedOrgId]);

  const isDeleted = !!(post?.deleted_at || post?.deletedAt);
  const canShareWorkplace = !!(
    resolvedOrgId &&
    resolvedPostId &&
    !isDeleted &&
    !writeApis.isActAsOrg
  );
  const canShareSocial = !!(
    isSocialPost &&
    resolvedPostId &&
    !isDeleted &&
    writeApis.allowShareToDm
  );
  const canShare = canShareWorkplace || canShareSocial;
  const canSaveWorkplace = !!(
    resolvedOrgId &&
    resolvedPostId &&
    !isDeleted &&
    !writeApis.isActAsOrg
  );
  const canSaveSocial = !!(
    isSocialPost &&
    resolvedPostId &&
    !isDeleted &&
    writeApis.allowSave
  );
  const canSave = canSaveWorkplace || canSaveSocial;
  const canSocialRepost = !!(
    socialRepost &&
    accessToken &&
    rootRepostId &&
    !isDeleted &&
    writeApis.allowAmplify
  );
  /** Outer quote cards: bare amplify only — Quote lives on the nested original. */
  const isQuoteWrapper = post?.repost_kind === "quote";
  /** Bare never on your own content; Quote still allowed on originals you wrote. */
  const canBareRepost = canSocialRepost && !isOwnPost;
  const canQuoteRepost = canSocialRepost && !isQuoteWrapper;
  const showRepostControl = canBareRepost || canQuoteRepost;
  const saved = canSave
    ? canSaveSocial && !canSaveWorkplace
      ? isSocialSaved(resolvedPostId)
      : isSaved(resolvedOrgId, resolvedPostId)
    : false;

  useEffect(() => {
    if (!canSave || !resolvedPostId) return;
    if (canSaveSocial && !canSaveWorkplace) {
      ensureSocialStatus(resolvedPostId);
    } else if (canSaveWorkplace) {
      ensureStatus(resolvedOrgId, resolvedPostId);
    }
  }, [
    canSave,
    canSaveSocial,
    canSaveWorkplace,
    ensureStatus,
    ensureSocialStatus,
    resolvedOrgId,
    resolvedPostId,
  ]);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      const next =
        canSaveSocial && !canSaveWorkplace
          ? await toggleSocialSaved(resolvedPostId)
          : await toggleSaved(resolvedOrgId, resolvedPostId);
      onSavedChange?.(resolvedPostId, next);
    } catch (err) {
      console.error("Failed to toggle saved post:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLike = async () => {
    if (isLiking || !onLike) return;
    setIsLiking(true);
    try {
      await onLike();
    } finally {
      setIsLiking(false);
    }
  };

  const toggleBareRepost = async () => {
    if (!canBareRepost || repostBusy) return;
    setRepostBusy(true);
    try {
      if (localReposted) {
        await axios.delete(writeApis.repostUrl(rootRepostId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setLocalReposted(false);
        setLocalRepostCount((n) => Math.max(0, n - 1));
        onRepostChange?.({
          viewer_has_reposted: false,
          repost_count: Math.max(0, localRepostCount - 1),
          unreposted: true,
          root_id: rootRepostId,
        });
        toastSuccess("Repost removed");
      } else {
        const res = await axios.post(
          writeApis.repostUrl(rootRepostId),
          { kind: "bare" },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setLocalReposted(true);
        setLocalRepostCount((n) => n + 1);
        onRepostChange?.({
          viewer_has_reposted: true,
          repost_count: localRepostCount + 1,
          unreposted: false,
          post: res.data,
          root_id: rootRepostId,
        });
        toastSuccess("Reposted");
      }
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not update repost"
      );
    } finally {
      setRepostBusy(false);
    }
  };

  const isInline = variant === "inline";
  const inlineBtn = isInline ? "gap-1 px-2" : "gap-2";
  const showReport = Boolean(accessToken && socialRepost);
  const showRightCluster = canSave || showReport;

  const likeButton = (
    <Button
      variant="ghost"
      size={isInline ? "sm" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        handleLike();
      }}
      disabled={isLiking}
      className={cn(
        "flex items-center",
        inlineBtn,
        isInline && "h-8 px-1.5 text-muted-foreground hover:text-foreground"
      )}
    >
      <Heart
        className={cn(
          "h-4 w-4",
          isLiking && "animate-pulse",
          isLiked && "text-red-500"
        )}
        fill={isLiked ? "red" : "none"}
      />
      <span>{likeCount}</span>
    </Button>
  );

  const commentButton = (
    <Button
      variant="ghost"
      size={isInline ? "sm" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        onComment?.();
      }}
      className={cn("flex items-center", inlineBtn)}
    >
      <MessageCircle className="h-4 w-4" />
      <span>{commentCount}</span>
    </Button>
  );

  const repostControl = showRepostControl ? (
    isQuoteWrapper ? (
      <Button
        variant="ghost"
        size={isInline ? "sm" : "default"}
        disabled={repostBusy}
        onClick={(e) => {
          e.stopPropagation();
          toggleBareRepost();
        }}
        className={cn(
          "flex items-center",
          inlineBtn,
          localReposted && "text-emerald-600"
        )}
        title={localReposted ? "Undo repost" : "Repost"}
        aria-label={localReposted ? "Undo repost" : "Repost"}
      >
        <Repeat2 className={cn("h-4 w-4", repostBusy && "animate-pulse")} />
        <span>{localRepostCount}</span>
      </Button>
    ) : canBareRepost ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size={isInline ? "sm" : "default"}
            disabled={repostBusy}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "flex items-center",
              inlineBtn,
              localReposted && "text-emerald-600"
            )}
            title="Repost"
            aria-label="Repost"
          >
            <Repeat2 className={cn("h-4 w-4", repostBusy && "animate-pulse")} />
            <span>{localRepostCount}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem
            disabled={repostBusy}
            onSelect={(e) => {
              e.preventDefault();
              toggleBareRepost();
            }}
          >
            {localReposted ? "Undo repost" : "Repost"}
          </DropdownMenuItem>
          {canQuoteRepost ? (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setQuoteOpen(true);
              }}
            >
              Quote
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <Button
        variant="ghost"
        size={isInline ? "sm" : "default"}
        onClick={(e) => {
          e.stopPropagation();
          setQuoteOpen(true);
        }}
        className={cn("flex items-center", inlineBtn)}
        title="Quote"
        aria-label="Quote"
      >
        <Repeat2 className="h-4 w-4" />
        <span>{localRepostCount}</span>
      </Button>
    )
  ) : null;

  const shareButton = canShare ? (
    <Button
      variant="ghost"
      size={isInline ? "sm" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        setShareOpen(true);
      }}
      className={cn("flex items-center", inlineBtn)}
      title="Send in a message"
      aria-label="Send in a message"
    >
      <Send className="h-4 w-4" />
      {!isInline && <span>Send</span>}
    </Button>
  ) : null;

  const saveButton = canSave ? (
    <Button
      variant="ghost"
      size={isInline ? "sm" : "default"}
      onClick={handleSave}
      disabled={isSaving}
      className={cn("flex items-center", inlineBtn)}
      title={saved ? "Unsave post" : "Save post"}
      aria-label={saved ? "Unsave post" : "Save post"}
      aria-pressed={saved}
    >
      <Bookmark
        className={cn(
          "h-4 w-4",
          isSaving && "animate-pulse",
          saved && "text-amber-600"
        )}
        fill={saved ? "currentColor" : "none"}
      />
      {!isInline && <span>{saved ? "Saved" : "Save"}</span>}
    </Button>
  ) : null;

  const reportButton = showReport ? (
    <Button
      variant="ghost"
      size={isInline ? "sm" : "default"}
      onClick={(e) => {
        e.stopPropagation();
        setReportOpen(true);
      }}
      className={cn("flex items-center", inlineBtn)}
      title="Report post"
      aria-label="Report post"
    >
      <Flag className="h-4 w-4" />
      {!isInline && <span>Report</span>}
    </Button>
  ) : null;

  return (
    <>
      <div
        className={cn(
          "flex w-full min-w-0 items-center",
          isInline
            ? "flex-nowrap gap-0.5 text-sm text-muted-foreground"
            : "flex-wrap gap-2 sm:gap-4 text-base",
          className
        )}
      >
        <div
          className={cn(
            "flex min-w-0 items-center",
            isInline ? "gap-0.5" : "flex-wrap gap-2 sm:gap-4"
          )}
        >
          {likeButton}
          {commentButton}
          {repostControl}
          {shareButton}
        </div>
        {showRightCluster ? (
          <div
            className={cn(
              "ml-auto flex shrink-0 items-center",
              isInline ? "gap-0.5" : "gap-2 sm:gap-4"
            )}
          >
            {saveButton}
            {reportButton}
          </div>
        ) : null}
      </div>

      {canShareWorkplace && (
        <SharePostToDmModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          orgId={resolvedOrgId}
          post={
            post || {
              post_id: postId,
              postId,
            }
          }
        />
      )}

      {canShareSocial && !canShareWorkplace && (
        <SharePostToSocialDmModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          post={
            post || {
              social_post_id: postId,
              post_id: postId,
              postId,
            }
          }
        />
      )}

      {canQuoteRepost ? (
        <SocialQuoteRepostDialog
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          accessToken={accessToken}
          rootPost={rootPostForRepost}
          actAsOrgId={writeApis.isActAsOrg ? writeApis.actAsOrgId : null}
          orgDisplayName={writeApis.displayName}
          ownedCommunities={writeApis.orgProfile?.communities || []}
          onCreated={(created) => {
            setLocalRepostCount((n) => n + 1);
            onQuoteCreated?.(created);
            onRepostChange?.({
              viewer_has_reposted: localReposted,
              repost_count: localRepostCount + 1,
              quote: created,
              root_id: rootRepostId,
            });
          }}
        />
      ) : null}

      {accessToken && socialRepost ? (
        <SocialReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          accessToken={accessToken}
          targetKind="post"
          postId={postId}
        />
      ) : null}
    </>
  );
};

export default PostInteractions;
